import { authClient } from "@d12labs/auth/client";
import { apiFetch } from "@d12labs/client";
import { Button } from "@d12labs/ui/button";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";

type EnabledProviders = {
	google: boolean;
	github: boolean;
	microsoft: boolean;
};

const PROVIDER_LABELS: Record<keyof EnabledProviders, string> = {
	google: "Google",
	github: "GitHub",
	microsoft: "Microsoft",
};

// Hits the public /api/account/providers endpoint to find out which OAuth
// providers are wired up server-side. Used by /login (and /signup when
// SIGNUP_ENABLED) so we only render buttons that will actually work.
//
// Renders nothing while loading and nothing if all three are disabled —
// the surrounding form is the fallback.
export function OauthButtons({ callbackURL = "/home" }: { callbackURL?: string }) {
	const { data } = useQuery({
		queryKey: ["account", "providers"],
		queryFn: () => apiFetch<EnabledProviders>("/api/account/providers"),
		staleTime: Infinity,
	});

	if (!data) return null;
	const enabledKeys = (Object.keys(PROVIDER_LABELS) as Array<keyof EnabledProviders>).filter(
		(k) => data[k],
	);
	if (enabledKeys.length === 0) return null;

	const handleClick = async (provider: keyof EnabledProviders) => {
		const { error } = await authClient.signIn.social({ provider, callbackURL });
		// Better Auth normally navigates the browser to the provider's auth
		// URL — so this catch path is for cases where the provider misfires
		// before redirecting (network error, server config drift).
		if (error) toast.error(error.message ?? `${PROVIDER_LABELS[provider]} sign-in failed`);
	};

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-col gap-2">
				{enabledKeys.map((provider) => (
					<Button
						key={provider}
						type="button"
						variant="outline"
						className="w-full"
						onClick={() => handleClick(provider)}
					>
						Continue with {PROVIDER_LABELS[provider]}
					</Button>
				))}
			</div>
			<div className="flex items-center gap-2 text-xs text-muted-foreground">
				<div className="h-px flex-1 bg-hairline" />
				or
				<div className="h-px flex-1 bg-hairline" />
			</div>
		</div>
	);
}
